"use client";
import Heading from "@/components/ui/Heading";
import Paragraph from "@/components/ui/Paragraph";
import { PriceInfoHtmlShell } from "@/components/ui/PriceInfoItem";
import {
  buildZecToBtcSeoValues,
  buildZecToUsdSeoValues,
  replaceZecToBtcSeoVariables,
  replaceZecToUsdSeoVariables,
} from "@/utils/converterSeoVariables";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { kodeMonoBold } from "@/lib/kodeMonoBold";
import ConverterTabs from "./ConverterTabs";
import doubleArrow from "@/assets/icons/svg/double-arrow.svg";
import Image from "next/image";
import zecCoin from "@/assets/icons/svg/zec-coin.svg";
import btcCoin from "@/assets/icons/svg/bit-coin.svg";
import ethCoin from "@/assets/icons/svg/ethereum-coin.svg";
import usdCoin from "@/assets/icons/svg/usd-coin.svg";

import goldenGradient from "@/assets/images/golden-gradient.png";

type TabValue = "1 ZEC" | "10 ZEC" | "100 ZEC" | "1000 ZEC";

// 1. Define Supported Currencies & IDs for API
export const CURRENCIES = [
  { code: "ZEC", name: "Zcash", id: "zcash", icon: zecCoin },
  { code: "BTC", name: "Bitcoin", id: "bitcoin", icon: btcCoin },
  { code: "ETH", name: "Ethereum", id: "ethereum", icon: ethCoin },
  { code: "USD", name: "US Dollar", id: "usd", icon: usdCoin },
];
/**
 * Parse a converter slug like "zec-to-btc" → { from: ZEC entry, to: BTC entry }.
 * Falls back to ZEC / BTC when no match is found.
 */
function parseCurrenciesFromSlug(slug?: string) {
  if (slug) {
    const parts = slug.split("-to-");
    const fromCode = parts[0]?.toUpperCase();
    const toCode = parts[1]?.toUpperCase();
    const from = CURRENCIES.find((c) => c.code === fromCode) ?? CURRENCIES[0];
    const to = CURRENCIES.find((c) => c.code === toCode) ?? CURRENCIES[1];
    return { from, to };
  }
  return { from: CURRENCIES[0], to: CURRENCIES[1] };
}

const ConverterIndex = ({
  title,
  slug,
  aboutThisChart,
}: {
  title?: string;
  slug?: string;
  aboutThisChart?: string | null;
}) => {
  const [activeTab, setActiveTab] = useState<TabValue>("1 ZEC");
  const [inputValue, setInputValue] = useState("1");
  const [outputValue, setOutputValue] = useState("...");
  const [exchangeRate, setExchangeRate] = useState<number | null>(null);
  /** ZEC→default-quote rate for `aboutThisChart` — pinned to initial “to” currency, not live dropdown selection. */
  const [seoPinnedRate, setSeoPinnedRate] = useState<number | null>(null);
  const [isFetching, setIsFetching] = useState(true);

  // State for Dropdowns
  const [isFromDropdownOpen, setIsFromDropdownOpen] = useState(false);
  const [isToDropdownOpen, setIsToDropdownOpen] = useState(false);

  // Refs for closing dropdowns on outside click
  const fromDropdownRef = useRef<HTMLDivElement>(null);
  const toDropdownRef = useRef<HTMLDivElement>(null);

  // Derive initial currencies from the Strapi slug (e.g. "zec-to-btc")
  const { from: initialFrom, to: initialTo } = parseCurrenciesFromSlug(slug);
  const [fromCurrency, setFromCurrency] = useState(initialFrom);
  const [toCurrency, setToCurrency] = useState(initialTo);
  /** Initial “to” selector value (React keeps first mount only). */
  const [defaultDestinationCurrency] = useState(initialTo);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        fromDropdownRef.current &&
        !fromDropdownRef.current.contains(event.target as Node)
      ) {
        setIsFromDropdownOpen(false);
      }
      if (
        toDropdownRef.current &&
        !toDropdownRef.current.contains(event.target as Node)
      ) {
        setIsToDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const aboutHtml = useMemo(() => {
    if (!aboutThisChart) return null;
    const quoteCode = defaultDestinationCurrency.code;
    if (quoteCode === "BTC") {
      return replaceZecToBtcSeoVariables(
        aboutThisChart,
        buildZecToBtcSeoValues(seoPinnedRate)
      );
    }
    if (quoteCode === "USD") {
      return replaceZecToUsdSeoVariables(
        aboutThisChart,
        buildZecToUsdSeoValues(seoPinnedRate)
      );
    }
    return aboutThisChart;
  }, [aboutThisChart, defaultDestinationCurrency.code, seoPinnedRate]);

  // ZEC→initial default quote rate for `aboutThisChart` (independent of dropdown selection).
  useEffect(() => {
    const quote = defaultDestinationCurrency;
    if (quote.code !== "BTC" && quote.code !== "USD") {
      setSeoPinnedRate(null);
      return;
    }
    const zec = CURRENCIES.find((c) => c.code === "ZEC");
    if (!zec) {
      setSeoPinnedRate(null);
      return;
    }
    const fromId = zec.id;
    const toId = quote.id;

    let cancelled = false;
    const fetchPinnedSeoRate = async () => {
      try {
        const response = await fetch(
          `/api/convert?from=${encodeURIComponent(fromId)}&to=${encodeURIComponent(toId)}&amount=1`,
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (cancelled) return;
        if (typeof data.rate === "number") {
          setSeoPinnedRate(data.rate);
        } else {
          setSeoPinnedRate(null);
        }
      } catch (error) {
        console.error("Failed to fetch SEO-pinned converter rate:", error);
        if (!cancelled) setSeoPinnedRate(null);
      }
    };

    fetchPinnedSeoRate();
    const interval = setInterval(fetchPinnedSeoRate, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [defaultDestinationCurrency]);

  // 2. Dynamic API Fetch
  useEffect(() => {
    const fetchExchangeRate = async () => {
      setIsFetching(true);
      try {
        const fromId = fromCurrency.id;
        const toId = toCurrency.id;

        const response = await fetch(
          `/api/convert?from=${encodeURIComponent(fromId)}&to=${encodeURIComponent(toId)}&amount=1`,
        );

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const data = await response.json();

        if (typeof data.rate === "number") {
          setExchangeRate(data.rate);
        } else {
          setExchangeRate(null);
        }
      } catch (error) {
        console.error("Failed to fetch exchange rate:", error);
        setExchangeRate(null);
      } finally {
        setIsFetching(false);
      }
    };

    fetchExchangeRate();
    const interval = setInterval(fetchExchangeRate, 60000); // Refresh every 1 min
    return () => clearInterval(interval);
  }, [fromCurrency, toCurrency]);

  // Calculate Output
  useEffect(() => {
    if (exchangeRate === null) {
      setOutputValue("...");
      return;
    }

    const inputNum = parseFloat(inputValue);
    if (isNaN(inputNum)) {
      setOutputValue("0");
      return;
    }

    // Since we fetched the direct rate (From -> To), simply multiply
    const result = inputNum * exchangeRate;

    // formatting: if USD use 2 decimals, else 8 for crypto
    const decimals = toCurrency.code === "USD" ? 2 : 8;
    setOutputValue(result.toFixed(decimals));
  }, [inputValue, exchangeRate, toCurrency.code]);

  const handleTabChange = (tab: TabValue) => {
    setActiveTab(tab);
    const numericValue = tab.split(" ")[0];
    setInputValue(numericValue);

    // Force ZEC if tab is clicked
    if (fromCurrency.code !== "ZEC") {
      const zec = CURRENCIES.find((c) => c.code === "ZEC");
      if (zec) setFromCurrency(zec);
    }
  };

  const handleSwap = () => {
    setInputValue(outputValue);
    setOutputValue(inputValue);

    const temp = fromCurrency;
    setFromCurrency(toCurrency);
    setToCurrency(temp);
  };

  /** Stacking for dropdowns only — keep DOM order FROM → swap → TO (swap must not reorder rows). */
  const fromInputZMobile =
    isToDropdownOpen && !isFromDropdownOpen ? "max-sm:z-20" : "max-sm:z-[9999]";
  const toInputZMobile = isToDropdownOpen ? "max-sm:z-[9999]" : "max-sm:z-20";

  const handleFromSelect = (currency: typeof fromCurrency) => {
    setFromCurrency(currency);
    setIsFromDropdownOpen(false);
  };

  const handleToSelect = (currency: typeof toCurrency) => {
    setToCurrency(currency);
    setIsToDropdownOpen(false);
  };

  return (
    <div className={`${kodeMonoBold.variable} my-13.75`}>
      <div>
        <Heading className="text-white text-center font-rubik text-[28px] sm:text-[35px] font-bold leading-normal">
          {title ?? "Zcash Converter"}
        </Heading>
        <Paragraph className="text-white/50 mt-2 text-xs font-medium leading-[160%] font-kode-mono text-center">
          Percentage of shielded vs unshielded ZEC.
        </Paragraph>
      </div>
      <div className="flex justify-center mt-8 sm:mt-13">
        <ConverterTabs activeTab={activeTab} onTabChange={handleTabChange} />
      </div>
      <div className="mt-8.5 mb-15 flex justify-center gap-x-4 xl:gap-x-6.25 items-center flex-col sm:flex-row gap-y-6">
        {/* === FROM SECTION === */}
        <div
          className={`px-4.5 py-2.5 h-14 w-80 sm:w-70 xl:w-95 border border-white/5 bg-white/4 flex items-center gap-x-3.75 relative ${fromInputZMobile} sm:z-20`}
        >
          <input
            type="text"
            value={inputValue}
            onChange={(e) => {
              if (/^\d*\.?\d*$/.test(e.target.value))
                setInputValue(e.target.value);
            }}
            className="bg-transparent border-none outline-none text-white text-xl w-full placeholder-white/30 font-kode-mono font-normal leading-normal opacity-60 tracking-wide"
          />

          {/* FROM Dropdown Trigger */}
          <div className="relative h-full" ref={fromDropdownRef}>
            <button
              onClick={() => setIsFromDropdownOpen(!isFromDropdownOpen)}
              className="flex items-center gap-x-2.5 h-full shrink-0 cursor-pointer transition-colors rounded-r-sm hover:opacity-80"
            >
              <div className="size-5 rounded-full flex items-center justify-center overflow-hidden">
                <Image
                  src={fromCurrency.icon}
                  alt={fromCurrency.code}
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="text-white font-kode-mono-bold text-base leading-4 tracking-wide">
                {fromCurrency.code}
              </span>
              <svg
                width="10"
                height="6"
                viewBox="0 0 10 6"
                fill="none"
                className={`transition-transform duration-200 ${isFromDropdownOpen ? "rotate-180" : ""}`}
              >
                <path
                  d="M1 1L5 5L9 1"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {/* FROM Dropdown List */}
            {isFromDropdownOpen && (
              <div className="absolute top-full right-0 mt-2 w-32 bg-[#1A1A1A] border border-white/10 shadow-xl overflow-hidden z-50">
                {CURRENCIES.filter((c) => c.code !== toCurrency.code).map((curr) => (
                  <button
                    key={curr.code}
                    onClick={() => handleFromSelect(curr)}
                    className="w-full flex items-center gap-x-3 px-4 py-3 hover:bg-white/10 transition-colors text-left cursor-pointer"
                  >
                    <div className="size-4 rounded-full flex items-center justify-center overflow-hidden shrink-0">
                      <Image
                        src={curr.icon}
                        alt={curr.code}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span className="text-white font-kode-mono text-sm">
                      {curr.code}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <Image
            className="pointer-events-none absolute left-0 -bottom-13 z-[-1]"
            src={goldenGradient}
            alt="shape"
            quality={100}
          />
        </div>

        {/* Swap Button — must stack above input rows (z-9999) so taps aren’t stolen by overflow/gradients */}
        <button
          type="button"
          onClick={handleSwap}
          className="max-sm:rotate-90 relative max-sm:z-[10001] sm:z-50 size-8.75 rounded-full border border-white/10 bg-white/15 hover:bg-white/20 flex justify-center items-center cursor-pointer transition-all duration-200"
        >
          <Image src={doubleArrow} alt="Double arrow" quality={100} />
        </button>

        {/* === TO SECTION === */}
        <div
          className={`px-4.5 py-2.5 h-14 w-80 sm:w-70 xl:w-95 border border-white/5 bg-white/4 flex items-center justify-between relative ${toInputZMobile} sm:z-20`}
        >
          {isFetching ? (
            <div className="flex-1 flex items-center gap-1">
              <span className="size-1.5 rounded-full bg-white/50 animate-bounce [animation-delay:0ms]" />
              <span className="size-1.5 rounded-full bg-white/50 animate-bounce [animation-delay:150ms]" />
              <span className="size-1.5 rounded-full bg-white/50 animate-bounce [animation-delay:300ms]" />
            </div>
          ) : (
            <input
              type="text"
              value={outputValue}
              readOnly
              className="bg-transparent border-none outline-none text-white text-xl w-full placeholder-white/30 font-kode-mono font-normal leading-normal opacity-60 tracking-wide"
            />
          )}

          {/* TO Dropdown Trigger */}
          <div className="relative h-full" ref={toDropdownRef}>
            <button
              onClick={() => setIsToDropdownOpen(!isToDropdownOpen)}
              className="flex items-center gap-x-2.5 h-full shrink-0 cursor-pointer transition-colors rounded-r-sm hover:opacity-80"
            >
              <div className="size-5 rounded-full flex items-center justify-center overflow-hidden">
                <Image
                  src={toCurrency.icon}
                  alt={toCurrency.code}
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="text-white font-kode-mono-bold text-base leading-4 tracking-wide">
                {toCurrency.code}
              </span>
              <svg
                width="10"
                height="6"
                viewBox="0 0 10 6"
                fill="none"
                className={`transition-transform duration-200 ${isToDropdownOpen ? "rotate-180" : ""}`}
              >
                <path
                  d="M1 1L5 5L9 1"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {/* TO Dropdown List */}
            {isToDropdownOpen && (
              <div className="absolute top-full right-0 mt-2 w-32 bg-[#1A1A1A] border border-white/10 shadow-xl overflow-hidden z-50">
                {CURRENCIES.filter((c) => c.code !== fromCurrency.code).map((curr) => (
                  <button
                    key={curr.code}
                    onClick={() => handleToSelect(curr)}
                    className="w-full flex items-center gap-x-3 px-4 py-3 hover:bg-white/10 transition-colors text-left cursor-pointer"
                  >
                    <div className="size-4 rounded-full flex items-center justify-center overflow-hidden shrink-0">
                      <Image
                        src={curr.icon}
                        alt={curr.code}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span className="text-white font-kode-mono text-sm">
                      {curr.code}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <Image
            className="pointer-events-none absolute left-0 -bottom-13 z-[-1]"
            src={goldenGradient}
            alt="shape"
            quality={100}
          />
        </div>
      </div>

      <Paragraph className="text-white font-kode-mono text-xs font-medium leading-[160%] text-center [&>span]:text-white/50">
        <span>Last updated</span> {new Date().toLocaleString()}
      </Paragraph>
      {aboutHtml != null && (
        <PriceInfoHtmlShell contentHtml={aboutHtml} proseClassName="seo-copy-paragraphs" />
      )}
    </div>
  );
};

export default ConverterIndex;
